/*
 * Pure helpers of the staff engagement page (OVIO-110). The filter lives in the query string so staff
 * can bookmark a group (?ids=12,57,301); the CSV link carries the same parameters.
 */

const GREEN = '#1E7B34';
const RED = '#C62828';
const GREY = '#5F6B76';
const LIGHT = '#9AA3AB';

export function formatNumber(value) {
  if (value === null || value === undefined) {
    return '–';
  }
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export function trendView(trend) {
  if (trend === null || trend === undefined) {
    return { text: '–', arrow: '', color: LIGHT };
  }
  if (trend === 'uusi') {
    return { text: 'uusi', arrow: '', color: GREY };
  }
  if (trend > 0) {
    return { text: '+' + trend + ' %', arrow: '▲', color: GREEN };
  }
  if (trend < 0) {
    return { text: trend + ' %', arrow: '▼', color: RED };
  }
  return { text: '0 %', arrow: '', color: GREY };
}

export function parseQuery(search) {
  const params = new URLSearchParams(search || '');
  let group = 'all';
  if (params.has('chain')) {
    group = 'chain';
  } else if (params.has('ids')) {
    group = 'ids';
  } else if (params.has('pilot')) {
    group = 'pilot';
  }
  return {
    group,
    chain: params.get('chain') || '',
    ids: params.get('ids') || '',
    q: params.get('q') || '',
    // The pilot panel's start week (Korjaamot tab); it belongs to the group it was chosen for.
    start_week: params.get('start_week') || '',
    // Valmistajahaut tab: period, month and the workshop opened for its month report.
    period: params.get('period') || '',
    month: params.get('month') || '',
    workshop: params.get('workshop') || '',
  };
}

export function buildQuery(state) {
  const parts = [];
  if (state.group === 'chain' && state.chain) {
    parts.push('chain=' + encodeURIComponent(state.chain));
  } else if (state.group === 'ids' && state.ids) {
    parts.push('ids=' + encodeURIComponent(state.ids));
  } else if (state.group === 'pilot') {
    parts.push('pilot=1');
  }
  if (state.q) {
    parts.push('q=' + encodeURIComponent(state.q));
  }
  if (state.start_week) {
    parts.push('start_week=' + encodeURIComponent(state.start_week));
  }
  ['period', 'month', 'workshop'].forEach((key) => {
    if (state[key]) {
      parts.push(key + '=' + encodeURIComponent(state[key]));
    }
  });
  return parts.length ? '?' + parts.join('&') : '';
}

export function weekNumber(label) {
  return String(parseInt(label.split('-W')[1], 10));
}

export function weekRange(weeks) {
  const first = weeks[0];
  const last = weeks[weeks.length - 1];
  const firstYear = first.split('-W')[0];
  const lastYear = last.split('-W')[0];
  if (firstYear === lastYear) {
    return weekNumber(first) + '–' + weekNumber(last) + '/' + lastYear;
  }
  return weekNumber(first) + '/' + firstYear + '–' + weekNumber(last) + '/' + lastYear;
}

export function formatDate(iso) {
  // Helsinki calendar date: the server's week axis is Helsinki too.
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Helsinki', day: 'numeric', month: 'numeric', year: 'numeric',
  }).formatToParts(new Date(iso));
  const get = type => parts.filter(p => p.type === type)[0].value;
  return parseInt(get('day'), 10) + '.' + parseInt(get('month'), 10) + '.' + get('year');
}

const MONTHS = ['tammikuu', 'helmikuu', 'maaliskuu', 'huhtikuu', 'toukokuu', 'kesäkuu', 'heinäkuu', 'elokuu',
  'syyskuu', 'lokakuu', 'marraskuu', 'joulukuu'];

// Milliseconds as seconds with one decimal and a Finnish comma; '–' when there is no value.
export function secondsBare(ms) {
  if (ms === null || ms === undefined) {
    return '–';
  }
  return (ms / 1000).toFixed(1).replace('.', ',');
}

export function seconds(ms) {
  return ms === null || ms === undefined ? '–' : secondsBare(ms) + ' s';
}

// '31.10. 15.42' in Helsinki time.
export function shortTime(iso) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Helsinki', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(iso));
  const get = type => parts.filter(p => p.type === type)[0].value;
  return parseInt(get('day'), 10) + '.' + parseInt(get('month'), 10) + '. ' + get('hour') + '.' + get('minute');
}

// '2026-10' -> 'lokakuu' (the staff page's months are written in Finnish).
export function monthName(label) {
  return MONTHS[parseInt(label.split('-')[1], 10) - 1];
}

export function previousMonth(label) {
  const [year, month] = label.split('-').map(n => parseInt(n, 10));
  return month === 1 ? (year - 1) + '-12' : year + '-' + String(month - 1).padStart(2, '0');
}
